import { Avatar, Box, Grid, Paper, Stack, TableContainer, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import React from "react";
import { useSearchParams } from "react-router-dom";
import CardHeaderTitle from "../../components/card-header-title/index.jsx";
import AppBarChart from "../../components/chart/bar.jsx";
import AppPieChart from "../../components/chart/pie.jsx";
import WPMChartComponent from "../../components/chart/wpm-chart.js";
import InfoCardBox from "../../components/info-card-box/index.js";
import InsightsCard from "../../components/insights/insights-card.js";
import { useThemeMode } from "../../components/theme/index.jsx";
import Topics from "../../components/topics/Topics.js";
import { THEME_MODES } from "../../helpers/constant.js";
import { getDurationString, roundNumberHelper, removeDetectKeywordFromVisuals } from "../../helpers/index.js";
import Utils from "../../utils";
import VIcon from "../images/Logo_black.png";
import { toggleButtonStyle } from "../../consts.js";

let agentSpeaker = "Speaker 2";
let clientSpeaker = "Speaker 1";

function CampaignInsights() {
  const [sentiment, setSentiment] = React.useState([]);
  const [callCount, setCallCount] = React.useState(0);

  const [params] = useSearchParams();
  const [campaignId] = React.useState(params.get("id") || "");

  const [data, setData] = React.useState({});
  const [spinner, setSpinner] = React.useState(false);
  const [pickedAgent, setPickedAgent] = React.useState(true);
  const [dataLoaded, setDataLoaded] = React.useState(false);
  const { colours, themeMode } = useThemeMode();

  async function getCampaignData() {
    Utils.sendProgressBar(true);
    setSpinner(true);

    try {
      // Attempt to fetch campaign data
      let response = await Utils.genericGet("getCampaign", campaignId);
      let s = response?.data?.getCampaign || {}; // Fallback to an empty object if response is undefined or null

      // Parse and assign default values if any property is missing
      s.VShits = JSON.parse(s?.VShits || "{}");
      s.agenthits = JSON.parse(s?.agenthits || "[]");
      s.alerthits = JSON.parse(s?.alerthits || "[]");
      s.durationhits = JSON.parse(s?.durationhits || "{}");
      s.topichits = JSON.parse(s?.topichits || "[]");
      s.sentimenthits = JSON.parse(s?.sentimenthits || "{}");
      s.trackerhits = JSON.parse(s?.trackerhits || "[]");
      s.questionhits = JSON.parse(s?.questionhits || "[]");
      s.analyticshits = JSON.parse(s?.analyticshits || "{}");

      // Ensure `sentimenthits` contains the required keys
      const sentimenthits = s.sentimenthits || {};
      const totalsentiment =
        (sentimenthits.neg || 0) +
        (sentimenthits.pos || 0) +
        (sentimenthits.neu || 0);

      setSentiment(
        totalsentiment > 0
          ? [
            {
              name: "Negative",
              Negative: roundNumberHelper(((sentimenthits.neg || 0) / totalsentiment) * 100).toFixed(0),
            },
            {
              name: "Neutral",
              Neutral: roundNumberHelper(((sentimenthits.neu || 0) / totalsentiment) * 100).toFixed(0),
            },
            {
              name: "Positive",
              Positive: roundNumberHelper(((sentimenthits.pos || 0) / totalsentiment) * 100).toFixed(0),
            },
          ]
          : [] // If totalsentiment is 0, set an empty array
      );

      setData(s);

      // Fetch call count
      let count = 0;
      const filter = {
        campaignConversationsId: { eq: campaignId },
        archive: { ne: true },
      };

      let counter = await Utils.genericListMOD(
        "listConversations",
        { filter, limit: 1000 },
        true
      );

      count += counter?.data?.listConversations?.items?.length || 0;
      let tkn = counter?.data?.listConversations?.nextToken;

      while (tkn) {
        counter = await Utils.genericListMOD(
          "listConversations",
          { filter, limit: 1000, nextToken: tkn },
          true
        );
        count += counter?.data?.listConversations?.items?.length || 0;
        tkn = counter?.data?.listConversations?.nextToken;
      }

      setCallCount(count);
    } catch (error) {
      console.error("Error fetching campaign data:", error);
    } finally {
      setSpinner(false);
    }
  }


  React.useEffect(() => {
    if (!dataLoaded) {
      const fetchData = async () => {
        await getCampaignData(); // Your async data fetching function
        setDataLoaded(true); // Mark data as loaded
      };
      fetchData();
    }

    window.scrollTo(0, 0); // Scroll to top when component mounts
  }, [dataLoaded]);

  //fix for over 100% changes speaker analytics
  let silencetest = 0; // Initialize with a default value

  if (data.analyticshits) {
    const agentTalkTime = data.analyticshits.agenttalktime || 0;
    const clientTalkTime = data.analyticshits.clienttalktime || 0;
    const silenceTime = data.analyticshits.silence || 0;

    // Calculate the total for validation
    const totalTalkTime = agentTalkTime + clientTalkTime + silenceTime;

    // Adjust silence if necessary
    silencetest = Math.max(0, silenceTime);
  } else {
    // Default to 0 if analyticshits is missing
    silencetest = 0;
  }

  const getCampaignDuration = () => {
    if (data?.durationhits && Object.keys(data.durationhits).length > 0) {
      const avgSeconds = data.durationhits.duration / data.durationhits.n;
      return getDurationString(avgSeconds);
    }
    return 0;
  }

  // Cards Data
  const cardsData = [
    {
      icon: "user",
      title: "# of Agents",
      tooltip: "Number of agents assigned to this campaign",
      value: data.agenthits ? data.agenthits.length : 0,
      backgroundColor: colours.blue10,
      borderColor: colours.blue20,
      iconBg: colours.blue100,
    },
    {
      icon: "calendar",
      title: "Total Calls",
      tooltip: "Total calls made in this campaign",
      value: callCount === 0 ? (data?.conversations?.items?.length || 0) : callCount,
      backgroundColor: colours.purple5,
      borderColor: colours.purple20,
      iconBg: colours.purple100,
    },
    {
      icon: "watch",
      title: "Avg. Call Duration",
      tooltip: "Average call duration for this campaign",
      value: getCampaignDuration(),
      backgroundColor: colours.success5,
      borderColor: colours.success20,
      iconBg: colours.success100,
    },
    {
      customAvatar: <Avatar sx={(theme) => ({ bgcolor: theme.palette.mode === THEME_MODES.LIGHT ? colours.white : colours.black })}><img src={VIcon} alt="Vocalysd Avatar" loading="lazy" width={18} height={22} /></Avatar>,
      title: "Avg. Vocalysd Score",
      tooltip: "Average Vocalysd score for this campaign",
      customValue: <Typography variant="h5" component="div" fontWeight={600} color={colours.success100}>{(Object.keys(data?.VShits || {}).length > 0) ? roundNumberHelper(data.VShits.VS / data.VShits.n) : 0}%</Typography>,
      backgroundColor: colours.orange10,
      borderColor: colours.orange20,
    },
  ];

  return (
    <Grid
      className="vocalysed-home-upadted campaigns-insights"
      container
      spacing={0}
      direction="row"
      flexGrow={1}
      padding={3}
      gap={3}
      overflow="auto"
      alignItems="start"
      sx={{ overflowX: "hidden" }}
    >
      {/* Top Cards Section */}
      <Grid
        item
        xs={12}
        padding={3}
        sx={{ border: `1px solid ${colours.grayDark500}`, borderRadius: 2.5, boxShadow: "none", backgroundColor: colours.white }}
        component={Paper}
      >
        <Grid
          container
          spacing={3}
          direction="row"
        >
          {cardsData.map((card, index) => (
            <Grid key={index} item xs={12} sm={6} lg={3}>
              <InfoCardBox
                colours={colours}
                themeMode={themeMode}
                icon={card.icon}
                title={card.title}
                tooltip={card.tooltip}
                value={card.value}
                backgroundColor={card.backgroundColor}
                borderColor={card.borderColor}
                iconBg={card.iconBg}
                customAvatar={card.customAvatar}
                customValue={card.customValue}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid
        container
        spacing={0}
        direction="row"
        gap={3}
      >
        <Grid item xs={12} lg sx={{ minWidth: "1px" }} display="flex" flexDirection="column" gap={3}>
          <Grid
            container
            flexWrap={{ sm: "nowrap" }}
            justifyContent="space-between"
            gap={3}
          >
            <Paper
              sx={{ display: "flex", flexDirection: "column", width: { xs: '100%', sm: '50%' }, borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
            >
              <CardHeaderTitle
                icon="smile"
                title="Sentiment"
                tooltip="Sentiment graph showing the overall sentiment trends for this campaign"
              />

              <Box marginTop={2} height={150}>
                <AppBarChart
                  data={sentiment}
                />
              </Box>
            </Paper>
            <Paper
              sx={{ display: "flex", flexDirection: "column", width: { xs: '100%', sm: '50%' }, borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
            >
              <CardHeaderTitle
                icon="alert-triangle"
                title="Alerts"
                tooltip="Campaign alerts showing critical updates and notifications"
              />

              <Box marginTop={2}>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {!data.alerthits
                    ? null
                    : data.alerthits.map((z, zi) => (
                      <Chip
                        key={zi}
                        label={removeDetectKeywordFromVisuals(z.alert)}
                        size="small"
                        color="warning"
                      />
                    ))}
                </Stack>
              </Box>
            </Paper>
          </Grid>
          <InsightsCard data={data} colours={colours} themeMode={themeMode} />
          <Paper
            sx={{ display: "flex", flexDirection: "column", width: '100%', borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
          >
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={.5}
              justifyContent="space-between"
              alignItems="center"
            >
              <CardHeaderTitle
                icon="check-circle"
                title="Top Questions"
                tooltip="Top questions identified from campaign conversations, based on frequency"
              />

              <ButtonGroup
                variant="contained"
                aria-label="outlined primary button group"
                className="buttons-border-extend"
              >
                <Button
                  onClick={() => setPickedAgent(true)}
                  style={toggleButtonStyle(pickedAgent, colours, themeMode)}
                >
                  Agent
                </Button>
                <Button
                  onClick={() => setPickedAgent(false)}
                  style={toggleButtonStyle(!pickedAgent, colours, themeMode)}
                >
                  Client
                </Button>
              </ButtonGroup>
            </Stack>
            <Box marginTop={2}>
              <TableContainer>
                <Table size="small" aria-label="Top Questions">
                  <TableBody>
                    {!data.questionhits ? (
                      <TableRow />
                    ) : (
                      data.questionhits
                        .filter((z) =>
                          pickedAgent
                            ? z.speaker === clientSpeaker
                            : z.speaker === agentSpeaker
                        )
                        .sort((a, b) => b.n - a.n)
                        .slice(0, 10)
                        .map((z, zi) => (
                          <TableRow hover key={zi}>
                            <TableCell
                              style={{
                                paddingTop: 3,
                                paddingBottom: 3,
                              }}
                            >
                              {zi + 1 + ". " + z.text}
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} lg sx={{ minWidth: "1px" }} display="flex" flexDirection="column" gap={3}>
          <Grid
            container
            flexWrap={{ sm: "nowrap" }}
            justifyContent="space-between"
            gap={3}
          >
            <Paper
              sx={{ display: "flex", flexDirection: "column", width: { xs: '100%', sm: '50%' }, borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
            >
              <CardHeaderTitle
                icon="pie-chart"
                title="Speaker Analytics"
                tooltip="Speaker analytics providing insights on individual speaker performance"
              />

              <Box height={166}>
                <AppPieChart
                  data={!data.analyticshits
                    ? []
                    : [
                      {
                        name: "Agent",
                        value: data.analyticshits?.agenttalktime || 0,
                      },
                      {
                        name: "Client",
                        value: data.analyticshits?.clienttalktime || 0,
                      },
                      {
                        name: "Silence",
                        value: silencetest || 0,
                      },
                    ]}
                />
              </Box>
            </Paper>

            <Paper
              sx={{ display: "flex", flexDirection: "column", width: { xs: '100%', sm: '50%' }, borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
            >
              <CardHeaderTitle
                icon="check-circle"
                title="Avg. WPM Agent vs Client"
                tooltip="Average words per minute (WPM) comparison between agent and client in this campaign"
              />
              <Box marginTop={1} height={150}>
                <WPMChartComponent type={"campaign"} data={data.analyticshits} />
              </Box>
            </Paper>

          </Grid>

          <Paper
            sx={{ display: "flex", flexDirection: "column", width: '100%', borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
          >
            <CardHeaderTitle
              icon="message-circle"
              title="Topics"
              tooltip="The top topics discussed in this campaign based on conversation analysis"
            />

            <Topics
              topichits={data.topichits}
              maxItems={20}
            />
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default CampaignInsights;